// TODO: Error handling is missing from many of these functions.  Need to add it. Should be const { data, error } = await postTable('posts') --- and then include the error if there is one. Inside Blog.svelte, we can then check for error and display a message to the user.
import { imageBucket, postTable, digTable, locationTable } from '$lib/supabase';
import { validate as uuidValidate } from 'uuid';
import { postOwner, posts, batchUpdatePosts, clearPosts } from '.';
import type { PostAttrs, ImpactArray, PostType } from '.';
import DOMPurify from 'dompurify';
import { getUser } from '$lib/utils/user';
import { supabaseClient } from '$lib/supabase';
import { SecureSanitizer } from '$lib/utils/secureSanitizer';
import { get } from 'svelte/store';
import { user } from '$lib/user';
import { measureQueryPerformance } from '$lib/utils/performance';

const GET_COL_SET = '*'; // include all columns

const POST_SELECT_COLUMNS = `
slug, title, location, topic, image_url, author, created_by, description, objective, created_at, published, membersonly, type, id, event_location, event_date_start, event_date_end, impacts
`;

// Simple cache for featured posts
interface CacheEntry {
	data: any[];
	timestamp: number;
}

const featuredPostsCache: CacheEntry = {
	data: [],
	timestamp: 0
};

const CACHE_DURATION = 10 * 60 * 1000; // Increased from 5 minutes to 10 minutes for better performance

interface FilterOptions {
	pageNum: number;
	pageSize: number;
	topic?: string;
	media?: string;
	location?: string;
	isPublic?: boolean;
	isPublished?: boolean;
	userId?: string;
	searchQuery?: string;
}

/**
 * getPost - Get a single post with the given ID
 *
 * @param <string>id - ID of post to get
 * @returns { data, error } where data is a single supabase post table object
 *
 * TODO: better validation of ID and return object
 *
 */
export const getPost = (id: string) => postTable('posts').select(GET_COL_SET).eq('id', id).single();

/**
 * boolSetPostPublished - Set post published to state with given id
 *
 * @param <string>id - ID of post to set published state on
 * @param <state>boolean - should the post be published?  true/false
 * @returns <PostgrestFilterBuilder> | <boolean>false if error or bad UUID.
 *
 * TODO: better validation of ID and return object
 *
 */
export const boolSetPostPublished = async (id: string, state: boolean) => {
	// console.log('setPublished:', id, state);
	const valid = uuidValidate(id);

	if (valid) {
		// console.log('setPublished: Valid UUID');
		const retVal = postTable('posts').update({ published: state }).match({ id: id }).select();

		// console.log(retVal);
		return retVal;
	} else {
		// console.log('setPublished: ID not a valid UUID');
		return false;
	}
};

/**
 * addDigs ; Adds or updates the number of digs for a post
 * @param id <string> The UUID of the post to update
 *  * @param user_id <string> The user UUID who dug the post
 * @param newDigs <number> The total number of digs to add
 * @returns <boolean> True if the operation was successful, false otherwise
 */
export const addDigs = async (id: string, user_id: string, newDigs: number): Promise<boolean> => {
	const valid = uuidValidate(id);

	if (!valid) {
		return false;
	}

	try {
		// Insert a new record
		const { error: insertError } = await digTable('digs').insert({
			post_id: id,
			digs: newDigs,
			user_id: user_id
		});

		if (insertError) {
			console.error('Error inserting new dig record:', insertError);
			return false;
		}

		return true;
	} catch (error) {
		console.error('Unexpected error:', error);
		return false;
	}
};

/**
 * getDigsCount ; Retrieves the number of digs for a given post
 * @param postId <string> The UUID of the post
 * @returns <number> The count of digs
 */
export const getDigsCount = async (postId: string): Promise<number> => {
	const validPostId = uuidValidate(postId);

	if (!validPostId) {
		console.error('Invalid postId');
		return 0;
	}

	const { count, error } = await digTable('digs')
		.select('*', { count: 'exact' })
		.eq('post_id', postId);
	// console.log('postId', postId);
	// console.log('Response:', { data, count, error });

	if (error) {
		console.error('Failed to get digs count', error);
		return 0;
	}

	return count ?? 0; // Return count if defined, otherwise 0
};

/**
 * setPostDigs ; Sets digs of post
 *
 * @param id <string> The UUID of the post to update
 * @param digs <number> number of digs
 * @returns <PostgrestFilterBuilder> object of the Supabase PostgreSQL query | <boolean>false if error.
 *
 * TODO: validate post ownership first? doing that sufficiently in editor before getting here.
 *       better return object error handling
 */
export const setPostDigs = async (id: string, digs: number) => {
	// console.log('setPostTitle:', id, title);
	const valid = uuidValidate(id);

	if (valid) {
		// console.log('setPostTitle: Valid UUID');
		const retVal = postTable('posts').update({ digs: digs }).match({ id: id }).select();

		// console.log(retVal);
		return retVal;
	} else {
		// console.log('setPostTitle: ID not a valid UUID');
		return false;
	}
};

/**
 * getMembersOnlyPostsLimited - Gets a set of members-only posts
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getMembersOnlyPostsLimited = async (pageNum: number, pageSize: number) => {
	const { data, error: downloadError } = await postTable('posts')
		.select(POST_SELECT_COLUMNS)
		.eq('published', true)
		.or('membersonly.eq.true,public.eq.false')
		.order('created_at', { ascending: false })
		.range(pageNum * pageSize, (pageNum + 1) * pageSize - 1);
	posts.update((post) => {
		for (let i = 0; i < data.length; i++) {
			post.push(data[i]);
		}
		if (downloadError) {
			throw downloadError;
		}
		return post;
	});
};

/**
 * getPublishedPostsLimited - Gets a smaller set of initial posts for page load speed
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getPublishedPostsLimited = async (pageNum: number, pageSize: number) => {
	const { data, error: downloadError } = await postTable('posts')
		.select(POST_SELECT_COLUMNS)
		.eq('published', true)
		.eq('public', true)
		.order('created_at', { ascending: false })
		.range(pageNum * pageSize, (pageNum + 1) * pageSize - 1);

	if (downloadError) {
		throw downloadError;
	}

	posts.update((currentPosts) => {
		return [...currentPosts, ...(Array.isArray(data) ? data : [])];
	});

	return (Array.isArray(data) ? data : []).length;
};

export const getPublishedPostsLimitedPerformant = async (pageNum: number, pageSize: number) => {
	const from = pageNum * pageSize;
	const to = from + pageSize - 1;

	// Add count to get total number of posts
	const {
		data,
		error: downloadError,
		count
	} = await postTable('posts')
		.select(
			'slug, title, location, topic, image_url, author, created_by, description, objective, created_at, published, membersonly, type, id, event_location, event_date_start, event_date_end, impacts',
			{ count: 'exact' } // Add exact count
		)
		.eq('published', true)
		.eq('public', true)
		.order('created_at', { ascending: false })
		.range(from, to);

	if (downloadError) {
		console.error('Error fetching posts:', downloadError);
		throw downloadError;
	}

	// Ensure we return an empty array if no data
	return {
		posts: data || [],
		total: count || 0
	};
};

export const getFilteredPosts = async ({
	pageNum,
	pageSize,
	topic,
	media,
	location,
	isPublic = true,
	isPublished = true,
	userId,
	searchQuery
}: FilterOptions) => {
	let query = postTable('posts')
		.select(
			'slug, title, location, topic, image_url, author, created_by, description, objective, created_at, published, membersonly, type, id, event_location, event_date_start, event_date_end, impacts, digs(digs)',
			{ count: 'exact' }
		)
		.eq('published', isPublished)
		.eq('public', isPublic)
		.order('created_at', { ascending: false });

	// Add filters conditionally
	if (topic && topic !== 'all') {
		query = query.eq('topic', topic);
	}
	if (media && media !== 'all') query = query.eq('type', media);
	if (location && location !== 'all') query = query.eq('location', location);
	if (userId) query = query.eq('created_by', userId);
	if (searchQuery) query = query.textSearch('title', searchQuery);

	try {
		// Get both data and count in a single query
		const {
			data,
			count,
			error: downloadError
		} = await query.range(pageNum * pageSize, (pageNum + 1) * pageSize - 1);

		if (downloadError) {
			console.error('Error fetching posts:', downloadError);
			throw downloadError;
		}

		return {
			posts: (Array.isArray(data) ? data : []).map((post) => ({
				...post,
				digs: Array.isArray(post.digs) && post.digs.length > 0 ? post.digs[0].digs : 0
			})),
			total: count || 0
		};
	} catch (error) {
		console.error('Error in getFilteredPosts:', error);
		throw error;
	}
};
// const {
// 	data,
// 	error: downloadError,
// 	count
// } = await query.range(pageNum * pageSize, (pageNum + 1) * pageSize - 1);

// if (downloadError) {
// 	console.error('Error fetching posts:', downloadError);
// 	throw downloadError;
// }

// // Return the same structure as getPublishedPostsLimitedPerformant
// return {
// 	posts: data || [],
// 	total: count || 0
// };
// };

/**
 * getAllPublishedPostsLimited - Gets a smaller set of initial posts for page load speed
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getAllPublishedPostsLimited = async (pageNum: number, pageSize: number) => {
	const { data, error: downloadError } = await postTable('posts')
		.select(POST_SELECT_COLUMNS)
		.eq('published', true)
		.order('created_at', { ascending: false })
		.range(pageNum * pageSize, (pageNum + 1) * pageSize - 1);
	posts.update((post) => {
		for (let i = 0; i < data.length; i++) {
			post.push(data[i]);
		}
		if (downloadError) {
			throw downloadError;
		}
		return post;
	});
};

/**
 * getPublishedPostsLimited - Gets a smaller set of initial posts for page load speed
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getFeaturedPosts = async (pageNum: number, pageSize: number) => {
	try {
		// Check cache first
		const now = Date.now();
		if (featuredPostsCache.data.length > 0 && now - featuredPostsCache.timestamp < CACHE_DURATION) {
			// console.log('Using cached featured posts');
			const cachedData = featuredPostsCache.data.slice(
				pageNum * pageSize,
				(pageNum + 1) * pageSize
			);

			// Use optimized batch update
			batchUpdatePosts(cachedData);

			return { data: cachedData, error: null };
		}

		// Reduced timeout for faster failure detection
		const timeoutPromise = new Promise((_, reject) => {
			setTimeout(() => reject(new Error('Database query timeout')), 3000); // Reduced from 5 seconds to 3 seconds
		});

		const queryPromise = postTable('posts')
			.select(POST_SELECT_COLUMNS)
			.eq('featured', true)
			.eq('published', true)
			.eq('public', true)
			.order('created_at', { ascending: false })
			.range(pageNum * pageSize, (pageNum + 1) * pageSize - 1);

		// Race between query and timeout
		const { data, error: downloadError } = (await Promise.race([
			queryPromise,
			timeoutPromise
		])) as any;

		if (downloadError) {
			console.error('Error fetching featured posts:', downloadError);
			throw downloadError;
		}

		// Handle null data case
		const safeData = data || [];

		// Update cache with full dataset
		featuredPostsCache.data = safeData;
		featuredPostsCache.timestamp = now;

		// Use optimized batch update
		batchUpdatePosts(safeData);

		return { data: safeData, error: null };
	} catch (error) {
		console.error('getFeaturedPosts failed:', error);

		// Return empty data on error to prevent UI from breaking
		return { data: [], error };
	}
};

/**
 * getPublishedPostsLimited - Gets a smaller set of initial posts for page load speed
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getPublishedPostsRandom = async (pageNum: number, pageSize: number) => {
	const { data, error: downloadError } = await postTable('posts')
		.select(POST_SELECT_COLUMNS)
		// .eq('membersonly', false)
		.eq('public', true)
		.eq('published', true)
		.limit(6)
		.order('created_at', { ascending: false })
		.range(pageNum * pageSize, (pageNum + 1) * pageSize - 1);
	posts.update((post) => {
		for (let i = 0; i < data.length; i++) {
			post.push(data[i]);
		}
		if (downloadError) {
			throw downloadError;
		}
		return post;
	});
};

/**
 * getPublishedPosts - Gets all published posts
 *
 * @param <null>none - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getPublishedPosts = () =>
	postTable('posts')
		.select(POST_SELECT_COLUMNS)
		.eq('published', true)
		// .eq('membersonly', false)
		.eq('public', true);

/**
 * getNaturePostsLimited - Gets a smaller set of initial posts for page load speed
 *
 * @param <number>limit - no parameters
 * @returns { data, error: downloadError  } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getNaturePostsLimited = async (pageNum: number, pageSize: number) => {
	const { data, error: downloadError } = await postTable('posts')
		.select(POST_SELECT_COLUMNS)
		.eq('published', true)
		// .eq('membersonly', false)
		.eq('public', true)
		.in('topic', ['nature', 'food'])
		.order('created_at', { ascending: false })
		.range(pageNum * pageSize, (pageNum + 1) * pageSize - 1);
	posts.update((post) => {
		for (let i = 0; i < data.length; i++) {
			post.push(data[i]);
		}
		if (downloadError) {
			throw downloadError;
		}
		return post;
	});
};

/**
 * getEconomyPostsLimited - Gets a smaller set of initial posts for page load speed
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getEconomyPostsLimited = async (pageNum: number, pageSize: number) => {
	const { data, error: downloadError } = await postTable('posts')
		.select(POST_SELECT_COLUMNS)
		.eq('published', true)
		// .eq('membersonly', false)
		.eq('public', true)
		.eq('topic', 'economy')
		.order('created_at', { ascending: false })
		.range(pageNum * pageSize, (pageNum + 1) * pageSize - 1);
	posts.update((post) => {
		for (let i = 0; i < data.length; i++) {
			post.push(data[i]);
		}
		if (downloadError) {
			throw downloadError;
		}
		return post;
	});
};

/**
 * getWaterPostsLimited - Gets a smaller set of initial posts for page load speed
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getWaterPostsLimited = async (pageNum: number, pageSize: number) => {
	const { data, error: downloadError } = await postTable('posts')
		.select(POST_SELECT_COLUMNS)
		.eq('published', true)
		// .eq('membersonly', false)
		.eq('public', true)
		.eq('topic', 'water')
		.order('created_at', { ascending: false })
		.range(pageNum * pageSize, (pageNum + 1) * pageSize - 1);
	posts.update((post) => {
		for (let i = 0; i < data.length; i++) {
			post.push(data[i]);
		}
		if (downloadError) {
			throw downloadError;
		}
		return post;
	});
};

/**
 * getFreedomPostsLimited - Gets a smaller set of initial posts for page load speed
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getFreedomPostsLimited = async (pageNum: number, pageSize: number) => {
	const { data, error: downloadError } = await postTable('posts')
		.select(POST_SELECT_COLUMNS)
		.eq('published', true)
		// .eq('membersonly', false)
		.eq('public', true)
		.in('topic', ['freedom', 'economy'])
		.order('created_at', { ascending: false })
		.range(pageNum * pageSize, (pageNum + 1) * pageSize - 1);
	posts.update((post) => {
		for (let i = 0; i < data.length; i++) {
			post.push(data[i]);
		}
		if (downloadError) {
			throw downloadError;
		}
		return post;
	});
};

/**
 * getArtPostsLimited - Gets a smaller set of initial posts for page load speed
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getArtPostsLimited = async (pageNum: number, pageSize: number) => {
	const { data, error: downloadError } = await postTable('posts')
		.select(POST_SELECT_COLUMNS)
		.eq('published', true)
		// .eq('membersonly', false)
		.eq('public', true)
		.eq('topic', 'art')
		.order('created_at', { ascending: false })
		.range(pageNum * pageSize, (pageNum + 1) * pageSize - 1);
	posts.update((post) => {
		for (let i = 0; i < data.length; i++) {
			post.push(data[i]);
		}
		if (downloadError) {
			throw downloadError;
		}
		return post;
	});
};

/**
 * getEconomyPostsLimited - Gets a smaller set of initial posts for page load speed
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getTechPostsLimited = async (pageNum: number, pageSize: number) => {
	const { data, error: downloadError } = await postTable('posts')
		.select(POST_SELECT_COLUMNS)
		.eq('published', true)
		// .eq('membersonly', false)
		.eq('public', true)
		.eq('topic', 'tech')
		.order('created_at', { ascending: false })
		.range(pageNum * pageSize, (pageNum + 1) * pageSize - 1);
	posts.update((post) => {
		for (let i = 0; i < data.length; i++) {
			post.push(data[i]);
		}
		if (downloadError) {
			throw downloadError;
		}
		return post;
	});
};

/**
 * getPublishedTemplatesLimited - Gets a smaller set of initial posts for page load speed
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getPublishedTemplatesLimited = async (pageNum: number, pageSize: number) => {
	const { data, error: downloadError } = await postTable('posts')
		.select(POST_SELECT_COLUMNS)
		.eq('published', true)
		// .eq('membersonly', false)
		.eq('type', 'template')
		.order('created_at', { ascending: false })
		.range(pageNum * pageSize, (pageNum + 1) * pageSize - 1);
	posts.update((post) => {
		for (let i = 0; i < data.length; i++) {
			post.push(data[i]);
		}
		if (downloadError) {
			throw downloadError;
		}
		return post;
	});
};

/**
 * getPublishedTemplates - Gets all published action templates
 *
 * @param <null>none - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getPublishedTemplates = () =>
	postTable('posts')
		.select(POST_SELECT_COLUMNS)
		.eq('published', true)
		// .eq('membersonly', false)
		.eq('type', 'template');

export const getTitleBySlug = (slug: string) =>
	postTable('posts').select('title, created_by').eq('slug', slug).single();
/**
 * getPostsBySlug - Gets post by given URL slug
 *
 * example: https://example.com/blog/some_post_name the slug is "some_post_name"
 *
 * @param <string>slug - the URL slug matching the post
 * @returns { data, error } where data is a single supabase post table object matching the slug
 *
 * TODO: better validation of ID and return object
 *
 */
export const getPostBySlug = (slug: string) =>
	postTable('posts').select(GET_COL_SET).eq('slug', slug).single();

export const getPostIdBySlug = (slug: string) =>
	postTable('posts').select('id, created_by, title').eq('slug', slug).single();

/**
 * getPosts - Gets all posts
 *
 * @param <null>none - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getPosts = () => postTable('posts').select(GET_COL_SET);

/**
 * getPostsById - Gets all posts created by given ID of user
 *
 * @param <string>created_by - ID of user to get posts for
 * @returns { data, error } where data is a list of supabase post table objects belonging to the given user
 *
 * TODO: better validation of ID and return object
 *
 */
export const getPostsById = (created_by: string) =>
	postTable('posts').select(GET_COL_SET).eq('created_by', created_by);

// TODO: Make locations table in supabase and its own api
/**
 * getPostsByLocationLimited - Gets a smaller set of initial posts for page load speed
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getPostsByLocationLimited = async (
	location: string,
	pageNum: number,
	pageSize: number
) => {
	const from = pageNum * pageSize;
	const to = from + pageSize - 1;

	const {
		data,
		error: downloadError,
		count
	} = await postTable('posts')
		.select(
			'slug, title, location, topic, image_url, author, created_by, description, objective, created_at, published, membersonly, type, id, event_location, event_date_start, event_date_end, impacts',
			{ count: 'exact' }
		)
		.eq('published', true)
		.eq('public', true)
		.eq('location', location)
		.order('created_at', { ascending: false })
		.range(from, to);

	if (downloadError) {
		console.error('Error fetching posts:', downloadError);
		throw downloadError;
	}

	return {
		posts: data || [],
		total: count || 0
	};
};

/**
 * getPostsForNortheastLimited - Gets a smaller set of initial posts for page load speed
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getPostsForNortheastLimited = async (
	location: string,
	pageNum: number,
	pageSize: number
) => {
	const from = pageNum * pageSize;
	const to = from + pageSize - 1;

	const {
		data,
		error: downloadError,
		count
	} = await postTable('posts')
		.select(
			'slug, title, location, topic, image_url, author, created_by, description, objective, created_at, published, membersonly, type, id, event_location, event_date_start, event_date_end, impacts',
			{ count: 'exact' }
		)
		.or(`location.eq.${location},location.eq.Pollinator / Nomadic - Permatours`)
		.eq('published', true)
		.eq('public', true)
		// .eq('membersonly', false)
		.order('created_at', { ascending: false })
		.range(from, to);

	if (downloadError) {
		console.error('Error fetching posts:', downloadError);
		throw downloadError;
	}

	return {
		posts: data || [],
		total: count || 0
	};
};

// TODO: Make locations table in supabase and its own api
/**
 * getPublishedEventsByLocationLimited - Gets a smaller set of initial posts for page load speed
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getPublishedEventsByLocationLimited = async (
	location: string,
	pageNum: number,
	pageSize: number
) => {
	const { data, error: downloadError } = await postTable('posts')
		.select(
			'slug, title, location, topic, image_url, author, created_by, description, created_at, published, membersonly, type, id, event_location, event_date_start, event_date_end, impacts, event_location, event_date_start, event_date_end'
		)
		.eq('location', location)
		.eq('type', 'event')
		.eq('published', true)
		.order('created_at', { ascending: false })
		.range(pageNum * pageSize, (pageNum + 1) * pageSize - 1);
	posts.update((post) => {
		for (let i = 0; i < data.length; i++) {
			post.push(data[i]);
		}
		if (downloadError) {
			throw downloadError;
		}
		return post;
	});
};

// TODO: Make locations table in supabase and its own api
/**
 * getPostsByCityLimited - Gets a smaller set of initial posts for page load speed
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getPostsByCityLimited = async (
	location: string,
	pageNum: number,
	pageSize: number
) => {
	// console.log('getPostsByCityLimited called with:', { location, page, pageSize });

	const { data, error: downloadError } = await postTable('posts')
		.select(POST_SELECT_COLUMNS)
		.eq('location', location)
		.eq('published', true)
		// .eq('membersonly', false)
		.order('created_at', { ascending: false })
		.range(pageNum * pageSize, (pageNum + 1) * pageSize - 1);

	if (downloadError) {
		console.error('Error fetching posts:', downloadError);
		throw downloadError;
	}

	// console.log('Fetched data for location:', location, data);

	posts.update((post) => {
		for (let i = 0; i < data.length; i++) {
			post.push(data[i]);
		}
		return post;
	});

	// const updatedPosts = get(posts);
	// console.log('Updated posts store:', updatedPosts);
};

// TODO: Make locations table in supabase and its own api
/**
 * getPostsByCityLimited - Gets a smaller set of initial posts for page load speed
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getCreativePostsLimited = async (pageNum: number, pageSize: number) => {
	// console.log('getPostsByCityLimited called with:', { location, page, pageSize });

	const { data, error: downloadError } = await postTable('posts')
		.select(POST_SELECT_COLUMNS)
		.eq('location', 'Creative')
		.eq('published', true)
		// .eq('membersonly', false)
		.order('created_at', { ascending: false })
		.range(pageNum * pageSize, (pageNum + 1) * pageSize - 1);

	if (downloadError) {
		console.error('Error fetching posts:', downloadError);
		throw downloadError;
	}

	// console.log('Fetched data for location:', location, data);

	posts.update((post) => {
		for (let i = 0; i < data.length; i++) {
			post.push(data[i]);
		}
		return post;
	});

	// const updatedPosts = get(posts);
	// console.log('Updated posts store:', updatedPosts);
};

/**
 * getPostsByLocation - Gets all posts by given location ***ideally this would equal the local page slug?
 *
 * @param <string>location - location to get posts for
 * @returns { data, error } where data is a list of supabase post table objects that have that location (where the action or story took place)
 *
 * TODO: better validation of ID and return object
 *
 */
export const getPostsByLocation = (location: string) =>
	postTable('posts').select(POST_SELECT_COLUMNS).eq('location', location).eq('published', true);

/**
 * getMyPostsLimited - Gets a smaller set of initial posts for page load speed
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getMyPostsLimited = async (pageNum: number, pageSize: number) => {
	const from = pageNum * pageSize;
	const to = from + pageSize - 1;

	const {
		data,
		error: downloadError,
		count
	} = await postTable('posts')
		.select('*', { count: 'exact' })
		.eq('created_by', getUser().id)
		.order('created_at', { ascending: false })
		.range(from, to);

	if (downloadError) {
		console.error('Error fetching posts:', downloadError);
		throw downloadError;
	}

	return {
		posts: data || [],
		total: count || 0
	};
};

export const getMyPostDrafts = async (pageNum: number, pageSize: number) => {
	const from = pageNum * pageSize;
	const to = from + pageSize - 1;

	const {
		data,
		error: downloadError,
		count
	} = await postTable('posts')
		.select('*', { count: 'exact' })
		.eq('created_by', getUser().id)
		.eq('published', false)
		.order('created_at', { ascending: false })
		.range(from, to);

	if (downloadError) {
		console.error('Error fetching posts:', downloadError);
		throw downloadError;
	}

	return {
		posts: data || [],
		total: count || 0
	};
};

/**
 * getMyLatestPublishedPosts - Gets the latest 3 posts from the user where published = true
 *
 * @param <string>userId - The ID of the user whose posts you want to retrieve
 * @returns { data, error } where data is a list of posts
 */
export const getMyLatestPublishedPosts = async (userId: string) => {
	try {
		const { data, error } = await postTable('posts')
			.select('id, published, created_at, title, slug')
			.eq('created_by', userId) // Filter by the user's ID
			.eq('published', true) // Only fetch posts where published is true
			.order('created_at', { ascending: false }) // Sort by the created_at field (newest first)
			.limit(3); // Limit the result to 3 posts

		if (error) {
			throw error;
		}

		return { data, error: null };
	} catch (error) {
		console.error('Error fetching latest published posts:', error.message);
		return { data: null, error: 'Failed to retrieve posts' };
	}
};

/**
 * getMyPosts - Gets all posts belonging to a given user
 *
 * @param <null>none - no parameters
 * @returns { data, error } where data is a list of supabase post table objects belonging to the current user
 *
 */
export const getMyPosts = async () => await getPostsById(getUser().id);

/**
 * getMyValidatedActions - Gets all posts belonging to a given user
 *
 * @param <null>none - no parameters
 * @returns { data, error } where data is a list of supabase post table objects belonging to the current user
 *
 */
export const getMyValidatedActions = async () =>
	postTable('posts')
		.select(GET_COL_SET)
		.eq('created_by', getUser().id)
		.eq('published', true)
		// .eq('membersonly', false)
		.eq('actionvalidated', true);

/**
 * getMyActionsLimited - Gets a smaller set of initial posts for page load speed
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getMyActionsLimited = async (pageNum: number, pageSize: number) => {
	const { data, error: downloadError } = await postTable('posts')
		.select(POST_SELECT_COLUMNS)
		.eq('created_by', getUser().id)
		.eq('type', 'action')
		.order('created_at', { ascending: false })
		.range(pageNum * pageSize, (pageNum + 1) * pageSize - 1);
	posts.update((post) => {
		for (let i = 0; i < data.length; i++) {
			post.push(data[i]);
		}
		if (downloadError) {
			throw downloadError;
		}
		return post;
	});
};

/**
 * getMyActions - Gets all posts belonging to a given user
 *
 * @param <null>none - no parameters
 * @returns { data, error } where data is a list of supabase post table objects belonging to the current user
 *
 */
export const getMyActions = async () =>
	postTable('posts')
		.select(GET_COL_SET)
		.eq('created_by', getUser().id)
		.eq('published', true)
		.eq('type', 'action');

/**
 * getAllActions - Gets all action posts
 *
 * @param <null>none - no parameters
 * @returns { data, error } where data is a list of supabase post table objects belonging to the current user
 *
 */
export const getAllActions = async () =>
	postTable('posts').select(GET_COL_SET).eq('published', true).eq('type', 'action');

export const getAllActionsForKarma = async () => {
	// Fetch posts data
	const { data: postsData, error: postsError } = await postTable('posts')
		.select(
			`
		location,
		title,
		impacts,
		slug,
		event_date_start,
		event_date_end
	  `
		)
		.eq('published', true)
		.eq('type', 'action')
		.eq('actionvalidated', true)
		.eq('sent_to_karma', false)
		.neq('location', 'Puerto Jiménez, Osa Peninsula - Costa Rica');

	if (postsError) throw postsError;

	// Extract unique location names
	const locationNames = Array.from(new Set(postsData.map((post) => post.location)));

	// Fetch location data based on the location
	const { data: locationsData, error: locationsError } = await locationTable('locations')
		.select('placename, brandname, wallet, description')
		.in('placename', locationNames)
		.not('wallet', 'is', null)
		.neq('wallet', '');

	if (locationsError) throw locationsError;

	// Create a set of valid locations with wallets
	const validLocations = new Set(locationsData.map((loc) => loc.placename));

	// Filter posts and merge only those with valid locations
	const actions = postsData
		.filter((post) => validLocations.has(post.location)) // Include only posts with valid locations
		.map((post) => {
			const location = locationsData.find((loc) => loc.placename === post.location);
			return {
				project: location?.brandname,
				description: location?.description,
				walletaddress: location?.wallet,
				impact: {
					work: post.title,
					impact: post.impacts,
					proof: post.slug
				},
				started_at: post.event_date_start,
				completed_at: post.event_date_end
			};
		});

	// Sort actions by most recent event_date_start first
	actions.sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime());

	return { data: actions, error: null };
};

/**
 * updateKarma - Updates the sent_to_karma column to true
 *
 * @param <string>id - the Postgres ID of the post to update
 * @returns <PostgrestFilterBuilder> | <boolean>false if error.
 *
 * TODO: better return object error handling
 *
 *
 */
export const updateKarma = async (id: string) => {
	const valid = uuidValidate(id);

	if (valid) {
		const retVal = postTable('posts').update({ sent_to_karma: true }).match({ id: id });
		return retVal;
	}
};

/**
 * getViewedMemberActions - Gets all posts belonging to a given user
 *
 * @param <null>none - no parameters
 * @returns { data, error } where data is a list of supabase post table objects belonging to the current user
 *
 */
export const getViewedMemberActions = async (username) =>
	postTable('posts')
		.select(GET_COL_SET)
		.eq('author', username)
		.eq('published', true)
		// .eq('membersonly', false)
		.eq('type', 'action');

/**
 * getMyTemplates - Gets all posts belonging to a given user
 *
 * @param <null>none - no parameters
 * @returns { data, error } where data is a list of supabase post table objects belonging to the current user
 *
 */
export const getMyTemplates = async () =>
	postTable('posts').select(GET_COL_SET).eq('created_by', getUser().id).eq('type', 'template');

/**
 * updatePostContents - Updates the contents of a post
 *
 * @param <string>id - the Postgres ID of the post to update
 * @param <string>contents - the Quill Delta contents to update the post with
 * @returns <PostgrestFilterBuilder> | <boolean>false if error.
 *
 * TODO: better return object error handling
 *       validate Quill Delta object imput
 *
 */
export const updatePostContents = async (id: string, contents: string) => {
	const valid = uuidValidate(id);
	const CLEAN = DOMPurify.sanitize(contents);

	if (valid) {
		const retVal = postTable('posts').update({ contents: CLEAN }).match({ id: id });
		return retVal;
	}
};

// API CALLS FOR BOUNTIES

export const getPostClaimInfo = async (slugs: string[]) => {
	// console.log('Fetching claim info for slugs:', slugs);

	try {
		const { data, error } = await postTable('posts')
			.select('id, slug, created_by, contributor_members')
			.in('slug', slugs);
		if (error) {
			console.error('Supabase error:', error);
			return {};
		}
		// Convert array to map of slug -> author
		const claimInfoMap = (data || []).reduce(
			(acc: Record<string, { id: string; author: string; contributors: any }>, post) => {
				acc[post.slug] = {
					id: post.id,
					author: post.created_by,
					contributors: post.contributor_members
				};
				return acc;
			},
			{}
		);
		// console.log('Claim info found:', claimInfoMap);
		return claimInfoMap;
	} catch (error) {
		console.error('Unexpected error in getPostClaimInfo:', error);
		return {};
	}
};

interface Post {
	id: string;
	title: string;
	slug: string;
	claim_id: string;
	actionvalidated: boolean;
	contributor_members: any[];
}

export const getActionPostsForMemberWithClaimInfo = async (userId: string) => {
	try {
		const { data, error } = await postTable('posts')
			.select(
				`
                id,
                title,
                slug,
                claim_id,
                actionvalidated,
                contributor_members,
                reviews!post_id (
                    id,
                    status,
                    total_score
                )
            `
			)
			.eq('created_by', userId)
			.eq('type', 'action')
			.order('created_at', { ascending: false });

		if (error) {
			console.error('Error fetching member posts:', error);
			return [];
		}

		return (
			(data as unknown as Post[])?.map((post) => ({
				...post,
				contributors: post.contributor_members || [],
				review_status: post.reviews?.[0]?.status || null,
				review_score: post.reviews?.[0]?.total_score || null
			})) || []
		);
	} catch (err) {
		console.error('Unexpected error in getActionPostsForMemberWithClaimInfo:', err);
		return [];
	}
};

export const getPostTitlesByClaims = async (urls: string[]) => {
	try {
		// Extract slugs from full URLs
		const slugs = urls.map((url) => url.replace('https://bloomnetwork.earth/view/', ''));

		// Remove any duplicates
		const uniqueSlugs = [...new Set(slugs)];

		const { data, error } = await postTable('posts').select('slug, title').in('slug', uniqueSlugs);

		if (error) {
			console.error('Supabase error:', error);
			return {};
		}

		// Create a map of slug to title for easy lookup
		const titleMap = data.reduce<Record<string, string>>((acc, post) => {
			acc[post.slug] = post.title;
			return acc;
		}, {});

		return titleMap;
	} catch (error) {
		console.error('Unexpected error in getPostTitlesByClaims:', error);
		return {};
	}
};

export const setBountiesInfo = async (
	postId: string,
	claimId: string,
	contributors: { realname: string; hours: number }[]
) => {
	try {
		const valid = uuidValidate(postId);

		if (valid) {
			const CLEAN = await SecureSanitizer.sanitize(JSON.stringify(contributors));
			const CLEANJSON = JSON.parse(CLEAN);

			// First, check if the post exists and if we can read it
			const { data: existingPost, error: readError } = await postTable('posts')
				.select('id, created_by, title')
				.eq('id', postId)
				.single();

			if (readError) {
				console.error('Error reading post:', readError);
				return { error: readError, data: null };
			}

			if (!existingPost) {
				console.error('Post not found with ID:', postId);
				return { error: 'Post not found', data: null };
			}

			// Use the same simple pattern as other working functions
			const retVal = await postTable('posts')
				.update({
					contributor_members: CLEANJSON,
					bounty_posted: true,
					claim_id: claimId,
					actionvalidated: false,
					sent_to_karma: false
				})
				.match({ id: postId })
				.select();

			return retVal;
		} else {
			console.error('Invalid UUID for postId:', postId);
			return false;
		}
	} catch (error) {
		console.error('Error in setBountiesInfo:', error);
		throw error; // Re-throw the error after logging it
	}
};

export const checkPostClaim = async (postId: string) => {
	try {
		const { data, error } = await postTable('posts').select('claim_id').eq('id', postId).single();

		if (error) {
			console.error('Error checking post claim:', error);
			throw error;
		}

		return data?.claim_id || null;
	} catch (error) {
		console.error('Error in checkPostClaim:', error);
		throw error;
	}
};

// API for getting action posts FLO totals for grants round distribution
export interface ContributorMember {
	realname: string;
	hours: number;
}

export interface RoundDates {
	startDate: Date;
	endDate: Date;
}

export const ROUND_DATES: Record<number, RoundDates> = {
	1: {
		startDate: new Date('2023-01-01'),
		endDate: new Date('2024-07-14')
	},
	2: {
		startDate: new Date('2024-07-15'),
		endDate: new Date('2024-12-31')
	},
	3: {
		startDate: new Date('2025-01-01'),
		endDate: new Date('2025-03-31')
	},
	4: {
		startDate: new Date('2025-04-01'),
		endDate: new Date('2025-06-30')
	},
	5: {
		startDate: new Date('2025-07-01'),
		endDate: new Date('2025-09-30')
	},
	6: {
		startDate: new Date('2025-10-01'),
		endDate: new Date('2025-12-31')
	}
};

export const getActionPostsForRound = async (roundNumber: 1 | 2) => {
	const { startDate, endDate } = ROUND_DATES[roundNumber];

	const { data, error } = await postTable('posts')
		.select('id, contributor_members, created_at, location')
		.eq('type', 'action')
		// .eq('actionvalidated', true)
		.gte('created_at', startDate.toISOString())
		.lte('created_at', endDate.toISOString());

	if (error) {
		throw error;
	}
	// console.log('data for getActionPostsForRound', data);
	return data;
};

// End of round distribution api above
// End of bounties api

/**
 * getPostsByUsernameLimited - Gets a smaller set of initial posts for page load speed
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getPostsByUsernameLimited = async (id: string, pageNum: number, pageSize: number) => {
	const from = pageNum * pageSize;
	const to = from + pageSize - 1;

	const {
		data,
		error: downloadError,
		count
	} = await postTable('posts')
		.select(
			'slug, title, location, topic, image_url, author, created_by, description, objective, created_at, published, membersonly, type, id, event_location, event_date_start, event_date_end, impacts',
			{ count: 'exact' }
		)
		.eq('created_by', id)
		.eq('published', true)
		.order('created_at', { ascending: false })
		.range(from, to);

	if (downloadError) {
		console.error('Error fetching posts:', downloadError);
		throw downloadError;
	}

	return {
		posts: data || [],
		total: count || 0
	};
};

/**
 * voidCanEditPost - Checks if a user can edit post and stores bool to postOwner store
 *
 * @param <PostAttrs>post - the post to edit
 * @returns void - nada! Stores a bool to postOwner store.
 *
 * TODO: better return object error handling
 *
 */
export const voidCanEditPost = async (post: PostAttrs, profileId?: string) => {
	try {
		// validate uuid
		const valid = uuidValidate(post.created_by);
		if (valid) {
			// Use provided profileId if available, otherwise fall back to getUser().id
			let currentUserId: string | undefined;

			if (profileId) {
				currentUserId = profileId;
			} else {
				// Fallback to getUser() for backward compatibility
				const currentUser = getUser();
				currentUserId = currentUser?.id;
			}

			if (currentUserId && post.created_by === currentUserId) {
				postOwner.set(true); // set store true
			} else {
				// user didn't create post
				postOwner.set(false);
			}
		} else {
			postOwner.set(false);
			console.warn('voidCanEditPost: Invalid UUID for post.created_by', {
				postCreatedBy: post.created_by,
				postId: post.id
			});
		}
	} catch (error) {
		console.error('voidCanEditPost: Error checking post ownership', error);
		postOwner.set(false);
	}
};

/**
 *
 * @param file The picture we're saving to storage
 * @returns <PostgrestFilterBuilder> | <boolean>false if error.
 *
 * TODO: better validation on the file contents/size/dimensions/other meta
 *       better return object error handling
 */
export const updatePostImage = async (file: File, post: PostAttrs) => {
	const valid = uuidValidate(post.id);
	if (valid) {
		const owner = postOwner.subscribe((ret) => {
			return ret;
		});
		if (owner) {
			// console.log('updateImage: IS owner');
			const user = getUser();
			const fileExt = file.name.split('.').pop();
			const fileName = `${user.id}${Math.random()}.${fileExt}`;
			const filePath = `${fileName}`;

			const { error: uploadError } = await imageBucket('images').upload(filePath, file);
			if (uploadError) {
				throw uploadError;
			}

			// return await postTable('posts').upsert({ id: post.id, image_url: filePath });
			const retVal = postTable('posts')
				.update({ image_url: filePath })
				.match({ id: post.id })
				.select();

			// console.log(retVal);
			return retVal;
		} else {
			// console.log('updateImage: NOT owner');
			return {
				data: '',
				error: 'updateImage: NOT owner'
			};
		}
	} else {
		// console.log('updateImage: Post ID not valid');
		return {
			data: '',
			error: 'updateImage: PostID Not Valid'
		};
	}
};

/**
 *
 * @param url <string> - the Supabase URL string of the image to fetch
 * @returns <string> - a URL object string that can be used in an <img src={getImage('someImage')}> tag
 *
 * TODO: string validation
 *       better return object error handling
 */
export const getCardImage = async (url: string) => {
	const { data, error: downloadError } = await imageBucket('images').download(url, {
		transform: {
			width: 320,
			height: 144
		}
	});
	if (downloadError) {
		throw downloadError;
	}

	return URL.createObjectURL(data);
};
// TODO: return an image not a blob, so supabase can render a webp: https://supabase.com/docs/guides/storage/image-transformations#automatic-image-optimisation-webp

/**
 *
 * @param url <string> - the Supabase URL string of the image to fetch
 * @returns <string> - a URL object string that can be used in an <img src={getImageSm('someImage')}> tag
 *
 * TODO: string validation
 *       better return object error handling
 */
export const getPostImage = async (url: string) => {
	const { data, error: downloadError } = await imageBucket('images').download(url, {
		transform: {
			height: 384,
			resize: 'contain'
		}
	});
	if (downloadError) {
		throw downloadError;
	}
	return URL.createObjectURL(data);
};
// TODO: return an image not a blob, so supabase can render a webp: https://supabase.com/docs/guides/storage/image-transformations#automatic-image-optimisation-webp

// /**
//  *
//  * @param url <string> - the Supabase URL string of the image to fetch
//  * @returns <string> - a URL object string that can be used in an <img src={getImageSm('someImage')}> tag
//  *
//  * TODO: string validation
//  *       better return object error handling
//  */
// export const getImageXl = async (url: string) => {
// 	const { data, error: downloadError } = await imageBucket('images').download(url, {
// 		transform: {
// 			width: 800
// 		}
// 	});
// 	if (downloadError) {
// 		throw downloadError;
// 	}

// 	return URL.createObjectURL(data);
// };

/**
 *
 * @param post <PostAttrs> The post to update
 * @param postType <PostType> 'Action', 'Blog', 'Template', or 'Event'
 * @returns <PostgrestFilterBuilder> | <boolean>false if error.
 *
 * TODO: better return object error handling
 */
export const updatePostType = async (id: string, type: PostType) => {
	const CLEAN = await SecureSanitizer.sanitize(type);
	// console.log('ID', id);
	const valid = uuidValidate(id);
	if (valid) {
		const { data, error } = await postTable('posts').update({ type: CLEAN }).match({ id: id });
		return { data, error };
	}
};

/**
 *
 * @param id <string> The UUID of the post to update
 * @param isPublic <boolean> should the post be publicly accessible?
 * @returns <PostgrestFilterBuilder> object of the Supabase PostgreSQL query | <boolean>false if error.
 *
 * TODO: validate post ownership first? doing that sufficiently in editor before getting here.
 *       better return object error handling
 */
export const boolSetPostPublic = async (id: string, isPublic: boolean) => {
	// console.log('setPublic:', id, isPublic);
	const valid = uuidValidate(id);

	if (valid) {
		// console.log('setPublic: Valid UUID');
		const retVal = postTable('posts').update({ public: isPublic }).match({ id: id }).select();

		// console.log(retVal);
		return retVal;
	} else {
		// console.log('setPublic: ID not a valid UUID');
		return false;
	}
};

export const boolSetmembersonly = async (id: string, ismembersonly: boolean) => {
	// console.log('setmembersonly:', id, ismembersonly);
	const valid = uuidValidate(id);

	if (valid) {
		// console.log('setmembersonly: Valid UUID');
		const retVal = postTable('posts')
			.update({ membersonly: ismembersonly })
			.match({ id: id })
			.select();

		// console.log(retVal);
		return retVal;
	} else {
		// console.log('setmembersonly: ID not a valid UUID');
		return false;
	}
};

/**
 * setImpacts ; Sets Post Impacts for Regenerative Action post
 * @param id <string> The UUID of the post to update
 * @param impacts <ImpactArray> JSON impact object
 * @returns <PostgrestFilterBuilder> object of the Supabase PostgreSQL query | <boolean>false if error.
 *
 * TODO: validate post ownership first? doing that sufficiently in editor before getting here.
 *       better return object error handling
 */
export const setPostImpacts = async (id: string, impacts: ImpactArray) => {
	// console.log('setImpacts:', id, impacts);
	const valid = uuidValidate(id);

	if (valid) {
		// console.log('setImpacts: Valid UUID');
		const CLEAN = await SecureSanitizer.sanitize(JSON.stringify(impacts));
		const CLEANJSON = JSON.parse(CLEAN);
		const retVal = postTable('posts').update({ impacts: CLEANJSON }).match({ id: id }).select();

		// console.log(retVal);
		return retVal;
	} else {
		// console.log('setImpacts: ID not a valid UUID');
		return false;
	}
};

/**
 * setPostTitle ; Sets title of post
 *
 * @param id <string> The UUID of the post to update
 * @param title <string> title of the post
 * @returns <PostgrestFilterBuilder> object of the Supabase PostgreSQL query | <boolean>false if error.
 *
 * TODO: validate post ownership first? doing that sufficiently in editor before getting here.
 *       better return object error handling
 */
export const setPostTitle = async (id: string, title: string) => {
	// console.log('setPostTitle:', id, title);
	const valid = uuidValidate(id);

	if (valid) {
		// Check if JWT is set
		const jwt = localStorage.getItem('supabase_jwt');

		// Try to restore JWT if not present
		if (!jwt) {
			// console.log('setPostTitle - No JWT found, attempting to restore...');
			const { restoreSupabaseJWT } = await import('$lib/supabase');
			await restoreSupabaseJWT();
		}

		const CLEAN = await SecureSanitizer.sanitizeText(title);
		// console.log('setPostTitle: Valid UUID');
		const retVal = postTable('posts').update({ title: CLEAN }).match({ id: id }).select();

		// console.log(retVal);
		return retVal;
	} else {
		// console.log('setPostTitle: ID not a valid UUID');
		return false;
	}
};

/**
 * setPostLocation ; Sets location of post
 *
 * @param id <string> The UUID of the post to update
 * @param location <string> location of the post
 * @returns <PostgrestFilterBuilder> object of the Supabase PostgreSQL query | <boolean>false if error.
 *
 * TODO: validate post ownership first? doing that sufficiently in editor before getting here.
 *       better return object error handling
 */
export const setPostLocation = async (id: string, location: string) => {
	// console.log('setLocation:', id, location);
	const valid = uuidValidate(id);

	if (valid) {
		const CLEAN = await SecureSanitizer.sanitizeText(location);
		// console.log('setLocation: Valid UUID');
		const retVal = postTable('posts').update({ location: CLEAN }).match({ id: id }).select();

		// console.log(retVal);
		return retVal;
	} else {
		// console.log('setLocation: ID not a valid UUID');
		return false;
	}
};

/**
 * setPostEventDate ; Sets description of post
 *
 * @param id <string> The UUID of the post to update
 * @param event_date_start <Date> startdate of the event
 * * @param event_date_end <Date> enddate of the event
 * @returns <PostgrestFilterBuilder> object of the Supabase PostgreSQL query | <boolean>false if error.
 *
 * TODO: validate post ownership first? doing that sufficiently in editor before getting here.
 *       better return object error handling
 */
export const setPostEventDate = async (
	id: string,
	event_date_start: Date,
	event_date_end: Date
) => {
	const valid = uuidValidate(id);
	if (valid) {
		// Format dates as YYYY-MM-DD for Supabase date type
		const formatDate = (date: Date) => {
			const d = new Date(date);
			return d.toISOString().split('T')[0];
		};

		const retVal = postTable('posts')
			.update({
				event_date_start: formatDate(event_date_start),
				event_date_end: formatDate(event_date_end)
			})
			.match({ id: id })
			.select();

		return retVal;
	} else {
		return false;
	}
};

/**
 * setPostDescription ; Sets description of post
 *
 * @param id <string> The UUID of the post to update
 * @param description <string> description of the post
 * @returns <PostgrestFilterBuilder> object of the Supabase PostgreSQL query | <boolean>false if error.
 *
 * TODO: validate post ownership first? doing that sufficiently in editor before getting here.
 *       better return object error handling
 */
export const setPostDescription = async (id: string, description: string) => {
	// console.log('setDescription:', id, description);
	const valid = uuidValidate(id);

	if (valid) {
		const CLEAN = await SecureSanitizer.sanitize(description);
		// console.log('setDescription: Valid UUID');
		const retVal = postTable('posts').update({ description: CLEAN }).match({ id: id }).select();

		// console.log(retVal);
		return retVal;
	} else {
		// console.log('setDescription: ID not a valid UUID');
		return false;
	}
};

/**
 * setPostObjective ; Sets objective of post
 *
 * @param id <string> The UUID of the post to update
 * @param objective <string> objective of the post
 * @returns <PostgrestFilterBuilder> object of the Supabase PostgreSQL query | <boolean>false if error.
 *
 * TODO: validate post ownership first? doing that sufficiently in editor before getting here.
 *       better return object error handling
 */
export const setPostObjective = async (id: string, objective: string) => {
	// console.log('setObjective:', id, objective);
	const valid = uuidValidate(id);

	if (valid) {
		const CLEAN = await SecureSanitizer.sanitize(objective);
		// console.log('setObjective: Valid UUID');
		const retVal = postTable('posts').update({ objective: CLEAN }).match({ id: id }).select();

		// console.log(retVal);
		return retVal;
	} else {
		// console.log('setObjective: ID not a valid UUID');
		return false;
	}
};

/**
 * setPostEventTime ; Sets event time of post
 *
 * @param id <string> The UUID of the post to update
 * @param event_time_start <string> start time of the event
 *  * @param event_time_end <string> end time of the event
 * @returns <PostgrestFilterBuilder> object of the Supabase PostgreSQL query | <boolean>false if error.
 *
 * TODO: validate post ownership first? doing that sufficiently in editor before getting here.
 *       better return object error handling
 */
export const setPostEventTime = async (
	id: string,
	event_time_start: string,
	event_time_end: string
) => {
	// console.log('setEventTime:', id, description);
	const valid = uuidValidate(id);

	if (valid) {
		const CLEANSTART = await SecureSanitizer.sanitizeText(event_time_start);
		const CLEANEND = await SecureSanitizer.sanitizeText(event_time_end);
		// console.log('setEventTime: Valid UUID');
		const retVal = postTable('posts')
			.update({ event_time_start: CLEANSTART, event_time_end: CLEANEND })
			.match({ id: id })
			.select();

		// console.log(retVal);
		return retVal;
	} else {
		// console.log('setEventTime: ID not a valid UUID');
		return false;
	}
};

/**
 * setPostEventLocation ; Sets description of post
 *
 * @param id <string> The UUID of the post to update
 * @param event_location <string> where the event is taking place
 * @returns <PostgrestFilterBuilder> object of the Supabase PostgreSQL query | <boolean>false if error.
 *
 * TODO: validate post ownership first? doing that sufficiently in editor before getting here.
 *       better return object error handling
 */
export const setPostEventLocation = async (id: string, event_location: string) => {
	// console.log('setEventLocation:', id, event_location);
	const valid = uuidValidate(id);

	if (valid) {
		const CLEAN = await SecureSanitizer.sanitizeText(event_location);
		// console.log('setEventLocation: Valid UUID');
		const retVal = postTable('posts').update({ event_location: CLEAN }).match({ id: id }).select();

		// console.log(retVal);
		return retVal;
	} else {
		// console.log('setEventLocation: ID not a valid UUID');
		return false;
	}
};

/**
 * setparticipants ; Sets number of participants for a regenerative action post
 * @param id <string> The UUID of the post to update
 * @param participants <number> number of participants
 * @returns <PostgrestFilterBuilder> object of the Supabase PostgreSQL query | <boolean>false if error.
 *
 * TODO: validate post ownership first? doing that sufficiently in editor before getting here.
 *       better return object error handling
 */
export const setPostparticipants = async (id: string, participants: number) => {
	// console.log('setparticipants:', id, participants);
	const valid = uuidValidate(id);

	if (valid) {
		// console.log('setparticipants: Valid UUID');
		const retVal = postTable('posts')
			.update({ participants: participants })
			.match({ id: id })
			.select();

		// console.log(retVal);
		return retVal;
	} else {
		// console.log('setparticipants: ID not a valid UUID');
		return false;
	}
};

/**
 * setPosthours ; Sets number of hours spent per person
 * @param id <string> The UUID of the post to update
 * @param hours <number> number of hours spent
 * @returns <PostgrestFilterBuilder> object of the Supabase PostgreSQL query | <boolean>false if error.
 *
 * TODO: validate post ownership first? doing that sufficiently in editor before getting here.
 *       better return object error handling
 */
export const setPosthours = async (id: string, hours: number) => {
	// console.log('setparticipants:', id, hours);
	const valid = uuidValidate(id);

	if (valid) {
		// console.log('setparticipants: Valid UUID');
		const retVal = postTable('posts').update({ hours: hours }).match({ id: id }).select();

		// console.log(retVal);
		return retVal;
	} else {
		// console.log('setparticipants: ID not a valid UUID');
		return false;
	}
};

/**
 * setPostTopic ; Sets location of post
 *
 * @param id <string> The UUID of the post to update
 * @param topic <TopicType> topic of the post - see filter.type.ts for the typedef
 * @returns <PostgrestFilterBuilder> object of the Supabase PostgreSQL query | <boolean>false if error.
 *
 * TODO: validate post ownership first? doing that sufficiently in editor before getting here.
 *       better return object error handling
 */
export const setPostTopic = async (id: string, topic: string) => {
	// console.log('setTopic:', id, topic);
	const valid = uuidValidate(id);

	if (valid) {
		const CLEAN = await SecureSanitizer.sanitizeText(topic);
		// console.log('setTopic: Valid UUID');
		const retVal = postTable('posts').update({ topic: CLEAN }).match({ id: id }).select();

		// console.log(retVal);
		return retVal;
	} else {
		// console.log('setTopic: ID not a valid UUID');
		return false;
	}
};

/**
 * setPostCommunity ; Sets location of post
 *
 * @param id <string> The UUID of the post to update
 * @param community <string> community to set to
 * @returns <PostgrestFilterBuilder> object of the Supabase PostgreSQL query | <boolean>false if error.
 *
 * TODO: validate post ownership first? doing that sufficiently in editor before getting here.
 *       better return object error handling
 */
export const setPostCommunity = async (id: string, community: string) => {
	// console.log('setCommunity:', id, community);
	const valid = uuidValidate(id);

	if (valid) {
		const CLEAN = await SecureSanitizer.sanitizeText(community);
		// console.log('setCommunity: Valid UUID');
		const retVal = postTable('posts').update({ community: CLEAN }).match({ id: id }).select();

		// console.log(retVal);
		return retVal;
	} else {
		// console.log('setCommunity: ID not a valid UUID');
		return false;
	}
};

/**
 * setPostWebsite ; Sets location of post
 *
 * @param id <string> The UUID of the post to update
 * @param website <string> website to set to
 * @returns <PostgrestFilterBuilder> object of the Supabase PostgreSQL query | <boolean>false if error.
 *
 * TODO: validate post ownership first? doing that sufficiently in editor before getting here.
 *       better return object error handling
 */
export const setPostWebsite = async (id: string, website: string) => {
	// console.log('setWebsite:', id, website);
	const valid = uuidValidate(id);

	if (valid) {
		const CLEAN = await SecureSanitizer.sanitizeUrl(website);
		// console.log('setWebsite: Valid UUID');
		const retVal = postTable('posts').update({ website: CLEAN }).match({ id: id }).select();

		// console.log(retVal);
		return retVal;
	} else {
		// console.log('setWebsite: ID not a valid UUID');
		return false;
	}
};

/**
 * setPostValidated ; Sets verification status of action post
 * @param id <string> The UUID of the post to update
 * @param actionvalidated <bool> action is validated or not
 * @param verified <bool> action is verified or not
 * @returns <PostgrestFilterBuilder> object of the Supabase PostgreSQL query | <boolean>false if error.
 *
 *       better return object error handling
 */
export const setPostValidated = async (claimId: string, verified: boolean) => {
	// console.log('setparticipants:', id, hours);
	// const valid = uuidValidate(id);

	// if (valid) {
	// console.log('setparticipants: Valid UUID');
	const retVal = postTable('posts')
		.update({ actionvalidated: verified })
		.match({ claim_id: claimId })
		.select();

	// console.log(retVal);
	return retVal;
	// } else {
	// console.log('setparticipants: ID not a valid UUID');
	// return false;
	// }
};

/**
 * setPostValidated ; Sets verification status of action post
 * @param id <string> The UUID of the post to update
 * @param actionvalidated <bool> action is validated or not
 * @param needs_more_info <bool> action is verified or not
 * @returns <PostgrestFilterBuilder> object of the Supabase PostgreSQL query | <boolean>false if error.
 *
 *       better return object error handling
 */
export const setNeedsMoreInfo = async (id: string) => {
	// console.log('setparticipants:', id, hours);
	// const valid = uuidValidate(id);

	// if (valid) {
	// console.log('setparticipants: Valid UUID');
	const retVal = postTable('posts').update({ needs_more_info: true }).match({ id: id }).select();

	// console.log(retVal);
	return retVal;
	// } else {
	// console.log('setparticipants: ID not a valid UUID');
	// return false;
	// }
};

/**
 * createPost - Creates a post of the given type for the current user
 *
 * example: https://example.com/create/action the type is "action"
 *
 * @param <PostAttrs>post - post object (see post.type.ts)
 * @returns { data, error } where data is a single supabase post table object matching the post | false if err
 *
 * TODO: better validation of ID and return object
 *
 */
export const createPost = async (
	post: PostAttrs
): Promise<{ success: boolean; postId?: string }> => {
	// Debug: Check if title is a Promise
	if (post.title && typeof post.title === 'object' && 'then' in post.title) {
		console.error('createPost: Title is a Promise!', post.title);
		// Try to resolve it
		try {
			post.title = await (post.title as Promise<string>);
		} catch (error) {
			console.error('Error resolving title Promise in createPost:', error);
			post.title = '';
		}
	}

	if (uuidValidate(post.created_by)) {
		try {
			post.published = true;
			const { data, error } = await postTable('posts').insert(post).select('id');

			if (error) {
				console.error('Insert error:', error.message);
				return { success: false };
			} else if (data && data.length > 0) {
				return { success: true, postId: data[0].id };
			}
		} catch (err) {
			console.error('Unexpected error:', err.message);
			return { success: false };
		}
	} else {
		console.error('Invalid user ID');
		return { success: false };
	}
	return { success: false };
};
/**
 * createPost - Creates a post of the given type for the current user
 *
 * example: https://example.com/create/action the type is "action"
 *
 * @param <PostAttrs>post - post object (see post.type.ts)
 * @returns { data, error } where data is a single supabase post table object matching the post | false if err
 *
 * TODO: better validation of ID and return object
 *
 */
export const createPostDraft = async (post: PostAttrs) => {
	// Debug: Check if title is a Promise
	if (post.title && typeof post.title === 'object' && 'then' in post.title) {
		console.error('createPostDraft: Title is a Promise!', post.title);
		// Try to resolve it
		try {
			post.title = await (post.title as Promise<string>);
		} catch (error) {
			console.error('Error resolving title Promise in createPostDraft:', error);
			post.title = '';
		}
	}

	if (uuidValidate(post.created_by)) {
		try {
			post.published = false;
			const result = await postTable('posts').insert(post);
			if (result.error) {
				console.error('Insert error:', result.error.message);
				return false;
			} else {
				return true; // Indicate success
			}
		} catch (err) {
			console.error('Unexpected error:', err.message);
			return false; // Indicate failure
		}
	} else {
		console.error('Invalid user ID');
		return false; // Indicate failure
	}
};

/**
 * deletePost ; deletes post
 *
 * @param id <string> The UUID of the post to delete
 * @returns <PostgrestFilterBuilder> object of the Supabase PostgreSQL query, with error details if applicable
 *
 */
export const deletePost = async (id: string) => {
	let _data, _error;
	// make sure id is a uuid
	if (uuidValidate(id)) {
		// delete post with matching id. created_by must match user().id due to policy
		await postTable('posts').delete().match({ id });
	}
	// this is a trick to create a <PostgrestFilterBuilder> object if there's no matching record
	const data = _data;
	const error = _error;
	return { data, error };
};

/**
 * getSearch - Gets posts that match the user-entered query
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase post table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getSearch = async (query: string, pageNum: number, pageSize: number) => {
	const { data, error: downloadError } = await postTable('posts')
		.select(GET_COL_SET)
		.eq('published', true)
		// .eq('membersonly', false)
		// .filter('title', 'like', query)
		.textSearch('tsv', query, {
			type: 'websearch',
			config: 'english'
		})
		// const filterInfo = (term:string) => {
		//  dataItems.update(item => {
		//        item.filter(x => {
		//             return x.name.toLowerCase().includes(term.toLowerCase())
		//             || x.description.toLowerCase().includes(term.toLowerCase());
		//        })
		//        return dataItems;
		//     })
		// }*/

		// (column: string, operator: string, value: unknown)
		// BUG: it's just this textSearch that is not working to generate data. It also doesn't generate data if I plug in 'diamante'
		// .textSearch('tsv', `${query.trim()}`, {
		// 	type: 'websearch',
		// 	config: 'english'
		// })
		.order('created_at', { ascending: false })
		.range(pageNum * pageSize, (pageNum + 1) * pageSize - 1);
	// console.log(data);
	posts.update((post) => {
		for (let i = 0; i < data.length; i++) {
			post.push(data[i]);
		}
		if (downloadError) {
			throw downloadError;
		}
		return post;
	});
};

/**
 * getPublishedActionStatsForPeriod - Gets total published actions and unique authors in a date range
 * @param {Date} startDate - Start of period (inclusive)
 * @param {Date} endDate - End of period (inclusive)
 * @returns {Promise<{ totalActions: number, uniqueAuthors: number }>} - Stats object
 */
export const getPublishedActionStatsForPeriod = async (startDate: Date, endDate: Date) => {
	// For timestamptz, use start of day for start date and start of next day for end date
	const startDateStr = startDate.toISOString();
	const endDatePlusOne = new Date(endDate);
	endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);
	const endDateStr = endDatePlusOne.toISOString();

	const { data, count, error } = await postTable('posts')
		.select('created_by', { count: 'exact' })
		.eq('type', 'action')
		.eq('published', true)
		.gte('created_at', startDateStr)
		.lt('created_at', endDateStr);

	if (error) {
		console.error('Error getting published action stats for period:', error);
		return { totalActions: 0, uniqueAuthors: 0 };
	}

	const uniqueAuthors = new Set((data || []).map((post: any) => post.created_by)).size;
	return { totalActions: count || 0, uniqueAuthors };
};

/**
 * getUniqueLocationsForPublishedActionsInPeriod - Gets the number of unique locations (hubs) for published actions in a date range
 * @param {Date} startDate - Start of period (inclusive)
 * @param {Date} endDate - End of period (inclusive)
 * @returns {Promise<number>} - Number of unique locations
 */
export const getUniqueLocationsForPublishedActionsInPeriod = async (
	startDate: Date,
	endDate: Date
) => {
	// For timestamptz, use start of day for start date and start of next day for end date
	const startDateStr = startDate.toISOString();
	const endDatePlusOne = new Date(endDate);
	endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);
	const endDateStr = endDatePlusOne.toISOString();

	const { data, error } = await postTable('posts')
		.select('location')
		.eq('type', 'action')
		.eq('published', true)
		.gte('created_at', startDateStr)
		.lt('created_at', endDateStr);

	if (error) {
		console.error('Error getting unique locations for published actions:', error);
		return 0;
	}

	const uniqueLocations = new Set((data || []).map((post: any) => post.location)).size;
	return uniqueLocations;
};

export const getPublishedActionCountsByTopicInPeriod = async (
	topics: string[],
	startDate: Date,
	endDate: Date
) => {
	// For timestamptz, use start of day for start date and start of next day for end date
	const startDateStr = startDate.toISOString();
	const endDatePlusOne = new Date(endDate);
	endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);
	const endDateStr = endDatePlusOne.toISOString();

	// Single query to get all topics with counts
	const { data, error } = await postTable('posts')
		.select('topic')
		.eq('type', 'action')
		.eq('published', true)
		.in('topic', topics)
		.gte('created_at', startDateStr)
		.lt('created_at', endDateStr);

	if (error) {
		console.error('Error counting topics:', error);
		return topics.reduce((acc, topic) => ({ ...acc, [topic]: 0 }), {});
	}

	// Count occurrences of each topic
	const counts: Record<string, number> = {};
	topics.forEach((topic) => (counts[topic] = 0)); // Initialize all topics to 0

	(data || []).forEach((post: any) => {
		const topic = post.topic;
		if (topic && counts.hasOwnProperty(topic)) {
			counts[topic]++;
		}
	});

	return counts;
};
